import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import api from 'api';
import DocumentsIcon from 'icons/Documents.icon';
import { DataRoomNode } from '../../models/DataRoomNode';
import DocumentStatusBadge from 'components/DocumentStatusBadge';
import { DataRoomMember } from '../../models/DataRoomMember';
import { FileAccessSelector } from 'components/FileAccessSelector';
import { Checkbox } from 'ncoded-component-library';
import { getHasElementsInArray, getIsNodeFile, sortDataRoomNodes } from 'utils';
import { NodeChangedPermission } from 'components/FileAccessSelector/types';
import useCallbackAsync from 'hooks/useCallbackAsync';

import './MemberFileAccessSelector.styles.scss';

export const PARENT_CLASS = 'member-file-access-selector';

const DEFAULT_ACCESS = {
  hasFullAccess: false,
  hasFullDownloadAccess: false,
};

export type MemberFileAccessSelectorProps = {
  className?: string;
  dataRoomId: string;
  dataRoomMemberId?: string;
  refetchFlag?: boolean;
  isDisabled?: boolean;
  defaultOpen?: boolean;
  showSelectAllCheckbox?: boolean;
  updateMember?: (member: DataRoomMember) => void;
  refetch?: () => void;
};

const MemberFileAccessSelector: React.FC<MemberFileAccessSelectorProps> = (
  props,
) => {
  const {
    className,
    dataRoomId,
    dataRoomMemberId,
    refetchFlag,
    isDisabled,
    defaultOpen = false,
    showSelectAllCheckbox = true,
    refetch,
    updateMember,
  } = props;
  const [checkedFilesIds, setCheckedFilesIds] = useState<string[]>([]);
  const [checkedDownloadIds, setCheckedDownloadIds] = useState<string[]>([]);
  const [hasBaseNodes, setHasBaseNodes] = useState(false);

  const [access, setAccess] = useState(DEFAULT_ACCESS);

  const { hasFullAccess, hasFullDownloadAccess } = access || {};

  const classes = classNames(PARENT_CLASS, 'white-panel', className);

  const { t } = useTranslation();

  const isMixedAllViewAccess = useMemo(
    () => !hasFullAccess && getHasElementsInArray(checkedFilesIds),
    [checkedFilesIds, hasFullAccess],
  );
  const isMixedAllDownloadAccess = useMemo(
    () => !hasFullDownloadAccess && getHasElementsInArray(checkedDownloadIds),
    [checkedDownloadIds, hasFullDownloadAccess],
  );

  const [onHasFullAccessChange, isLoadingHasFullAccess] = useCallbackAsync(
    async (hasFullAccess: boolean) => {
      const { data } = await api.dataRoom.nodes[
        hasFullAccess
          ? 'grantFilesAccessWithoutUndo'
          : 'revokeFilesAccessWithoutUndo'
      ](dataRoomMemberId);

      updateMember?.(data);
      refetch();
    },
  );

  const [onHasFullDownloadAccessChange, isLoadingHasFullDownloadAccess] =
    useCallbackAsync(async (hasFullDownloadAccess: boolean) => {
      const { data } = await api.dataRoom.nodes[
        hasFullDownloadAccess
          ? 'grantDownloadFilesAccessWithoutUndo'
          : 'revokeDownloadFilesAccessWithoutUndo'
      ](dataRoomMemberId);

      updateMember?.(data);
      refetch();
    });

  const isLoading = isLoadingHasFullAccess || isLoadingHasFullDownloadAccess;

  const updateNodeIds = useCallback((dataRoomMember: DataRoomMember) => {
    const {
      uploadedFileIds,
      accessableDataRoomNodesView,
      accessableDataRoomNodesDownload,
      hasFullAccess,
      hasFullDownloadAccess,
    } = dataRoomMember;

    setCheckedFilesIds([...uploadedFileIds, ...accessableDataRoomNodesView]);
    setCheckedDownloadIds([
      ...uploadedFileIds,
      ...accessableDataRoomNodesDownload,
    ]);
    setAccess({ hasFullAccess, hasFullDownloadAccess });
  }, []);

  const getChildrenForNode = useCallback(
    async (id?: string) => {
      const {
        data: { dataRoomNodes, dataRoomMember },
      } = await api.dataRoom.nodes.getChildrenForMember(
        dataRoomId,
        id || null,
        dataRoomMemberId,
      );

      updateNodeIds(dataRoomMember);

      if (!id) setHasBaseNodes(getHasElementsInArray(dataRoomNodes));

      return sortDataRoomNodes(dataRoomNodes);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataRoomId, dataRoomMemberId, refetchFlag],
  );

  const updateFileAccess = useCallback(
    async (newNode: NodeChangedPermission) => {
      const {
        data: { dataRoomMember },
      } = await api.dataRoom.members.updateMemberDocuments(dataRoomMemberId, {
        changedPermissions: [newNode],
      });

      updateNodeIds(dataRoomMember);
      updateMember?.(dataRoomMember);
    },
    [dataRoomMemberId, updateMember, updateNodeIds],
  );

  const checkIfNodeIsDisabled = useCallback(
    (node: DataRoomNode) =>
      isDisabled ||
      isLoadingHasFullAccess ||
      isLoadingHasFullDownloadAccess ||
      (getIsNodeFile(node) && node.dataRoomOwner?._id === dataRoomMemberId),
    [
      isDisabled,
      isLoadingHasFullAccess,
      isLoadingHasFullDownloadAccess,
      dataRoomMemberId,
    ],
  );

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen={defaultOpen}
      trigger={
        <div className={`${PARENT_CLASS}__heading`}>
          <p className="heading">
            <DocumentsIcon />
            {t('dataRoom')}
          </p>
          {hasBaseNodes && (
            <DocumentStatusBadge
              numberOfCheckedFiles={checkedFilesIds.length}
            />
          )}
        </div>
      }
    >
      {hasBaseNodes && (
        <div className={`${PARENT_CLASS}__bulk-access`}>
          {showSelectAllCheckbox ? (
            <>
              <Checkbox
                disabled={isLoading}
                checked={hasFullAccess}
                className={classNames({ mixed: isMixedAllViewAccess })}
                label={t('view')}
                onChange={() => onHasFullAccessChange(!hasFullAccess)}
              />
              <Checkbox
                disabled={isLoading}
                checked={hasFullDownloadAccess}
                className={classNames({ mixed: isMixedAllDownloadAccess })}
                label={t('download')}
                onChange={() =>
                  onHasFullDownloadAccessChange(!hasFullDownloadAccess)
                }
              />
            </>
          ) : (
            <>
              <p>{t('view')}</p>
              <p>{t('download')}</p>
            </>
          )}
        </div>
      )}

      <FileAccessSelector
        hasLoading
        hasDownload
        refetchFlag={refetchFlag}
        checkedFilesIds={checkedFilesIds}
        checkedDownloadIds={checkedDownloadIds}
        getBaseNodes={getChildrenForNode}
        getChildrenForNode={getChildrenForNode}
        updateFileAccess={updateFileAccess}
        checkIfNodeIsDisabled={checkIfNodeIsDisabled}
      />
    </CollapsibleSection>
  );
};

export default MemberFileAccessSelector;
